import { action, computed, observable, toJS } from 'mobx';
import { AxiosError } from 'axios';
import {
  IAuthorizationToken,
  IAuthUser,
  UserPermissions
} from '@app/src/common/model/AuthModel';
import { DateTimeService } from '@app/src/services/DateTimeService';
import routerStore from '@app/src/common/store/RouterStore';
import userManagementRepository, {
  TokenString
} from '@app/src/common/repository/UserManagementRepository';
import notificationService from '@app/src/services/notification-service/NotificationService';
import UserPreferenceService from '@app/src/common/services/UserPreferenceService';
import {
  getLocationRequestHeader,
  resolveActiveLocationId,
  setLocationRequestHeader
} from '@app/src/common/repository/locationRequestHeader';
import { resolvePermissionsAtLocation } from '@app/src/common/store/effectivePermissions';
import AppStore from '@app/src/common/store/AppStore';
import {
  enableContentSharingKey,
  enableTenantManagementKey
} from '@app/src/components/user-management/constants/userModuleKeys';
import {
  CUSTOM_SOLUTIONS,
  USER_MODULE_KEY
} from '@app/src/common/model/AppModel';
import { ISolutionConfiguration } from '@app/src/common/model/CustomSolutionConfiguration';
import { SUPPORT_LOGIN_KEY } from '@app/src/common/constants/common';

const BROADCAST_CHANNEL_NAME = 'fw_auth';
const MSG_TOKEN_REFRESHED = 'token_refreshed';

type AuthBroadcastMessage = {
  type: typeof MSG_TOKEN_REFRESHED;
  issued_at: number;
};

class AuthStore {
  @observable token: IAuthorizationToken;
  @observable tokenStrJwtRaw: TokenString;
  @observable refreshTokenStrJwt: TokenString;
  @observable.shallow user: IAuthUser | null;
  idTokenStr: TokenString;
  private refreshRequest: Promise<{
    token: IAuthorizationToken;
    jwtToken: TokenString;
  }> | null;
  private refreshAbortController: AbortController | null = null;
  private broadcastChannel: BroadcastChannel | null = null;

  get tokenStrJwt(): TokenString {
    return this.tokenStrJwtRaw ? `Bearer ${this.tokenStrJwtRaw}` : null;
  }

  @computed get userPermissions(): UserPermissions[] {
    return this.permissionsForLocation(getLocationRequestHeader());
  }

  // Effective permissions at a Location: company-wide unioned with the Location's own permissions.
  // See resolvePermissionsAtLocation for the exact rule (company-wide always applies).
  private permissionsForLocation(locationId: string | null): UserPermissions[] {
    return resolvePermissionsAtLocation(
      this.user?.permissions,
      this.user?.locations,
      locationId
    ) as UserPermissions[];
  }

  @computed
  get userRoles(): string[] {
    return this.user?.roles || [];
  }

  @computed get userModule(): string[] {
    return this.user?.modules || [];
  }

  @computed get companyId(): string {
    return this.user?.companyId || '';
  }

  @computed get userId(): string {
    return this.user?.id || '';
  }

  @computed get isUserAuthValid(): boolean {
    return (
      this.token && DateTimeService.isTimestampNearToExpire(this.token.exp)
    );
  }

  @computed get isSuperAdmin(): boolean {
    return (
      this.hasPermission(UserPermissions.usermanagement_view_company_all) &&
      !this.isExternalUser
    );
  }

  @computed get isDeviceInspector(): boolean {
    return [
      UserPermissions.devicemanagement_view_device_company,
      UserPermissions.devicemanagement_view_device_all,
      UserPermissions.devicemanagement_view_device_own,
      UserPermissions.devicemanagement_view_group_all,
      UserPermissions.devicemanagement_view_group_company,
      UserPermissions.devicemanagement_view_group_own
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isDeviceAdmin(): boolean {
    return [
      UserPermissions.devicemanagement_create_device_all,
      UserPermissions.devicemanagement_update_device_all,
      UserPermissions.devicemanagement_create_device_company,
      UserPermissions.devicemanagement_update_device_company,
      UserPermissions.devicemanagement_create_device_own,
      UserPermissions.devicemanagement_update_device_own
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isEventManager(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.eventmanagement_view_event_tab,
      UserPermissions.eventmanagement_view_contact_tab,
      UserPermissions.eventmanagement_view_visitor_tab,
      UserPermissions.eventmanagement_view_company_tab,
      UserPermissions.eventmanagement_view_visitlog_tab,
      UserPermissions.eventmanagement_view_location_tab,
      UserPermissions.eventmanagement_view_onsite_tab,
      UserPermissions.eventmanagement_update_settings_company,
      UserPermissions.eventmanagement_view_reception_company,
      UserPermissions.eventmanagement_view_waitlist_tab,
      UserPermissions.eventmanagement_view_dashboard_tab
    ]);
  }

  @computed get isParkingViewer(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.parking_view_free_spaces_tab,
      UserPermissions.parking_view_bookings_tab,
      UserPermissions.parking_view_my_bookings_tab,
      UserPermissions.parking_view_logs_tab,
      UserPermissions.parking_view_settings_tab
    ]);
  }

  // Whether the caller may edit parking settings at the active Location. Shared by every parking
  // settings surface so the gate stays in one place.
  @computed get canEditParkingSettings(): boolean {
    return this.hasPermission(UserPermissions.parking_update_settings);
  }

  @computed get isCompanyAdmin(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.usermanagement_view_company_all,
      UserPermissions.usermanagement_view_user_all,
      UserPermissions.usermanagement_view_user_company
    ]);
  }

  @computed get isUserGroupViewer(): boolean {
    return (
      this.hasPermission(UserPermissions.usermanagement_view_user_group_tab) &&
      !!AppStore.configs.get(enableContentSharingKey)
    );
  }

  @computed get isCompanyUser(): boolean {
    return [
      UserPermissions.usermanagement_view_company_own,
      UserPermissions.usermanagement_view_user_own,
      UserPermissions.usermanagement_update_user_own,
      UserPermissions.usermanagement_view_role_own
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isScheduleViewUser(): boolean {
    return [
      UserPermissions.schedulemanagement_view_schedules_company,
      UserPermissions.schedulemanagement_view_schedules_own,
      UserPermissions.schedulemanagement_view_emergency_company
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isContentViewer(): boolean {
    return (
      this.isProjectViewer || this.isPlayListViewer || this.isShowBoardViewer
    );
  }

  @computed get isOrderViewer(): boolean {
    return [
      UserPermissions.ordermanagement_view_stations_all,
      UserPermissions.ordermanagement_view_stations_company,
      UserPermissions.ordermanagement_view_stations_own,
      UserPermissions.ordermanagement_view_location_company
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isBookingViewer(): boolean {
    return [
      UserPermissions.booking_view_bookings_tab,
      UserPermissions.booking_view_rooms_tab
    ].some(r => this.userPermissions.includes(r));
  }

  @computed get isInventoryViewer(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.inventory_view_dashboard_tab,
      UserPermissions.inventory_view_logs_company
    ]);
  }

  @computed get isWayFindingViewer(): boolean {
    return this.userPermissions
      .slice()
      .includes(UserPermissions.wayfinding_view_maps_company);
  }

  @computed get isStorageViewer(): boolean {
    return this.userPermissions.includes(
      UserPermissions.filestorage_view_gallery_company
    );
  }

  @computed get isProjectViewer(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.projectmanagement_view_projects_all,
      UserPermissions.projectmanagement_view_projects_company,
      UserPermissions.projectmanagement_view_projects_own,
      UserPermissions.contentmanagement_view_screenflow_in_workspace
    ]);
  }

  @computed get isShowBoardViewer(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.contentmanagement_view_showboards_all,
      UserPermissions.contentmanagement_view_showboards_own,
      UserPermissions.contentmanagement_view_showboards_company,
      UserPermissions.contentmanagement_view_showboards_in_workspace
    ]);
  }

  @computed get isPlayListViewer(): boolean {
    return this.hasOneOfPermissions([
      UserPermissions.contentmanagement_view_playlists_all,
      UserPermissions.contentmanagement_view_playlists_own,
      UserPermissions.contentmanagement_view_playlists_company,
      UserPermissions.contentmanagement_view_playlist_in_workspace
    ]);
  }

  @computed get isUseGroupCompanyViewer(): boolean {
    return this.userPermissions.includes(
      UserPermissions.usermanagement_view_user_group_company
    );
  }

  @computed get isFormManager(): boolean {
    return this.hasPermission(
      UserPermissions.formmanagement_view_template_company
    );
  }

  @computed get hasAllTenantAccess() {
    return (
      AppStore.configurablePages.get(enableTenantManagementKey) &&
      this.hasPermission(UserPermissions.access_all_tenant_entities)
    );
  }

  @computed
  get isExternalUser() {
    return this.user?.external;
  }

  @computed
  get isCustomSolutionsViewer(): boolean {
    const customSolutions = AppStore.moduleSettings
      .get(USER_MODULE_KEY)
      ?.get(CUSTOM_SOLUTIONS);
    if (!customSolutions) {
      return false;
    }
    try {
      const config: ISolutionConfiguration = JSON.parse(customSolutions);
      if (!config.roles?.length) {
        return true;
      }
      return config.roles.some(r => this.userRoles.includes(r));
    } catch (e) {
      return false;
    }
  }

  constructor() {
    this.token = this.getStoredToken();
    this.tokenStrJwtRaw = localStorage.getItem('JWT_TOKEN_SCHEMA');
    this.refreshTokenStrJwt = localStorage.getItem('JWT_REFRESH_TOKEN_SCHEMA');
    this.idTokenStr = localStorage.getItem('JWT_EXTERNAL_TOKEN_SCHEMA');
    this.initBroadcastChannel();
  }

  private initBroadcastChannel() {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (
        event: MessageEvent<AuthBroadcastMessage>
      ) => {
        if (event.data?.type !== MSG_TOKEN_REFRESHED) {
          return;
        }
        const issuedAt = event.data.issued_at ?? 0;
        if (issuedAt <= (this.token?.iat ?? 0)) {
          return;
        }
        // Sync first so the aborted request's `.catch` sees valid in-memory
        // tokens, then cancel our own doomed (single-use refresh) request.
        this.syncTokensFromStorage();
        this.refreshAbortController?.abort();
      };
    } catch {
      // BroadcastChannel construction failed — fall back to Layer A only
    }
  }

  @action
  private syncTokensFromStorage(): boolean {
    const storedJwt = localStorage.getItem('JWT_TOKEN_SCHEMA');
    if (!storedJwt || storedJwt === this.tokenStrJwtRaw) {
      return false;
    }
    try {
      const parsed = this.parseJwt(storedJwt);
      this.tokenStrJwtRaw = storedJwt;
      this.refreshTokenStrJwt = localStorage.getItem(
        'JWT_REFRESH_TOKEN_SCHEMA'
      );
      const storedIdToken = localStorage.getItem('JWT_EXTERNAL_TOKEN_SCHEMA');
      if (storedIdToken) {
        this.idTokenStr = storedIdToken;
      }
      this.token = parsed;
      return true;
    } catch {
      return false;
    }
  }

  // Layer A: returns true if localStorage holds a token different from the
  // in-memory one — another tab refreshed it. The caller should adopt
  // those tokens rather than initiate its own (single-use) refresh.
  private hasNewerStoredToken(): boolean {
    const storedJwt = localStorage.getItem('JWT_TOKEN_SCHEMA');
    return !!storedJwt && storedJwt !== this.tokenStrJwtRaw;
  }

  private broadcastTokenRefreshed() {
    if (!this.broadcastChannel || !this.token) {
      return;
    }
    try {
      const message: AuthBroadcastMessage = {
        type: MSG_TOKEN_REFRESHED,
        issued_at: this.token.iat
      };
      this.broadcastChannel.postMessage(message);
    } catch {
      // channel may be closed during teardown — safe to ignore
    }
  }

  async getTokenFromCode(code: string) {
    const response = await userManagementRepository.signInWithCode(code);
    this.setToken(
      response.data.access_token,
      response.data.refresh_token,
      response.data.id_token
    );
    this.setSupportLogin(false);
    return this.initCurrentUser();
  }

  hasPermissions(permissions: UserPermissions[]): boolean {
    return permissions.every(permission =>
      this.userPermissions.includes(permission)
    );
  }

  hasOneOfPermissions(permissions: UserPermissions[]): boolean {
    return permissions.some(permission =>
      this.userPermissions.includes(permission)
    );
  }

  hasPermission(permission: UserPermissions): boolean {
    return this.userPermissions.includes(permission);
  }

  // Location-explicit checks: gate on the caller's effective permissions at a specific Location
  // instead of the active (header-selected) one. Used by surfaces tied to a fixed Location — e.g.
  // the visit drawer's Parking tab, which follows the visit's Location, not the header selector.
  hasPermissionAtLocation(
    locationId: string | null,
    permission: UserPermissions
  ): boolean {
    return this.permissionsForLocation(locationId).includes(permission);
  }

  hasOneOfPermissionsAtLocation(
    locationId: string | null,
    permissions: UserPermissions[]
  ): boolean {
    const locationPermissions = this.permissionsForLocation(locationId);
    return permissions.some(permission =>
      locationPermissions.includes(permission)
    );
  }

  get isSupportLogin(): boolean {
    return localStorage.getItem(SUPPORT_LOGIN_KEY) === 'true';
  }

  @action
  setSupportLogin(value: boolean) {
    if (value) {
      localStorage.setItem(SUPPORT_LOGIN_KEY, 'true');
    } else {
      localStorage.removeItem(SUPPORT_LOGIN_KEY);
    }
  }

  @action
  setToken(
    tokenStrJwt: string,
    refreshTokenStrJwt: string,
    idTokenStr?: string
  ) {
    localStorage.setItem('JWT_TOKEN_SCHEMA', `${tokenStrJwt}`);
    localStorage.setItem('JWT_REFRESH_TOKEN_SCHEMA', `${refreshTokenStrJwt}`);
    if (idTokenStr) {
      localStorage.setItem('JWT_EXTERNAL_TOKEN_SCHEMA', `${idTokenStr}`);
      this.idTokenStr = `${idTokenStr}`;
    }
    this.tokenStrJwtRaw = `${tokenStrJwt}`;
    this.refreshTokenStrJwt = `${refreshTokenStrJwt}`;
    this.token = this.parseJwt(tokenStrJwt);
    this.setStoredToken(this.token);
    this.broadcastTokenRefreshed();
  }

  @action
  refreshToken(): Promise<{
    token: IAuthorizationToken;
    jwtToken: TokenString;
  }> {
    return userManagementRepository
      .refresh({
        refreshToken: this.refreshTokenStrJwt
      })
      .then<any, IAuthorizationToken>(response => {
        this.setToken(
          response.data.access_token,
          response.data.refresh_token,
          response.data.id_token
        );
        setTimeout(() => {
          window.location.reload();
        });
        return {
          token: toJS(this.token),
          jwtToken: toJS(this.tokenStrJwt)
        };
      });
  }

  @action
  checkAccess(
    needAuthRedirect = false
  ): Promise<{ token: IAuthorizationToken; jwtToken: TokenString }> {
    if (this.token && !this.isUserAuthValid) {
      if (!this.tokenStrJwtRaw) {
        setTimeout(() => {
          routerStore.gotoAuthentication();
        }, 5000);
        return Promise.reject(this.token);
      }

      // Layer A: another tab may have already refreshed — adopt its tokens
      // instead of initiating our own (single-use) refresh.
      if (this.hasNewerStoredToken() && this.syncTokensFromStorage()) {
        if (this.isUserAuthValid) {
          return Promise.resolve({
            token: toJS(this.token),
            jwtToken: toJS(this.tokenStrJwt)
          });
        }
      }

      if (!this.refreshRequest) {
        this.refreshAbortController = new AbortController();
        this.refreshRequest = userManagementRepository
          .refresh(
            { refreshToken: this.refreshTokenStrJwt },
            this.refreshAbortController.signal
          )
          .then<any, IAuthorizationToken>(response => {
            this.setToken(
              response.data.access_token,
              response.data.refresh_token,
              response.data.id_token
            );

            return {
              token: toJS(this.token),
              jwtToken: toJS(this.tokenStrJwt)
            };
          })
          .catch(error => {
            // Aborted by the broadcast handler: another tab broadcast a
            // fresh token and the handler already synced it into memory.
            // Return the in-memory tokens without retrying.
            if (error?.code === AxiosError.ERR_CANCELED) {
              if (this.isUserAuthValid) {
                return {
                  token: toJS(this.token),
                  jwtToken: toJS(this.tokenStrJwt)
                };
              }
            }
            // Race fallback for the no-BroadcastChannel path: another tab
            // refreshed but couldn't notify us. Adopt storage tokens
            // instead of clearing the session.
            this.syncTokensFromStorage();
            if (this.isUserAuthValid) {
              return {
                token: toJS(this.token),
                jwtToken: toJS(this.tokenStrJwt)
              };
            }
            if (this.tokenStrJwtRaw) {
              notificationService.warningMessage(
                'notification.session.expired'
              );
              routerStore.gotoAuthentication();
              this.clearData();
            }
          })
          .finally(() => {
            this.refreshRequest = null;
            this.refreshAbortController = null;
          });
      }
      return this.refreshRequest;
    }
    if (this.token) {
      return Promise.resolve({
        token: toJS(this.token),
        jwtToken: toJS(this.tokenStrJwt)
      });
    } else {
      if (needAuthRedirect) {
        routerStore.gotoAuthentication();
        return Promise.reject(true);
      }
      return Promise.reject(this.token);
    }
  }

  @action
  clearData() {
    userManagementRepository
      .signOut({
        refreshToken: this.refreshTokenStrJwt,
        idToken: this.idTokenStr || null
      })
      .then(result => {
        this.clearSavedData();
        if (result.data.logoutUrl) {
          window.location.href = result.data.logoutUrl;
        }
      })
      .catch(this.clearSavedData);
  }

  @action
  initCurrentUser() {
    if (!this.tokenStrJwtRaw) {
      return Promise.reject();
    }
    return userManagementRepository
      .getAuthedUserInfo()
      .then((user: IAuthUser) => {
        this.user = user;
        UserPreferenceService.savePreferencesToStorage(user.preferences);
        const storedLocationId = UserPreferenceService.getLocationId();
        const activeLocationId = resolveActiveLocationId(
          user.locations,
          storedLocationId
        );
        setLocationRequestHeader(activeLocationId);
        // If a stored preference pointed at a now-inaccessible location, the resolver fell
        // back to another one — persist that correction so the preference doesn't stay
        // stale across sessions.
        if (
          storedLocationId &&
          activeLocationId &&
          activeLocationId !== storedLocationId
        ) {
          UserPreferenceService.updateLocationId(activeLocationId);
        }
      })
      .catch(this.clearSavedData);
  }

  clearSavedData = () => {
    localStorage.clear();
    sessionStorage.clear();
    this.token = {} as any;
    this.tokenStrJwtRaw = null;
    this.user = null;
  };

  private getStoredTokenStr(): TokenString {
    return localStorage.getItem('JWT_TOKEN_STR');
  }

  private getStoredToken(): IAuthorizationToken {
    const JWT_TOKEN_STR = this.getStoredTokenStr();
    return JWT_TOKEN_STR ? JSON.parse(JWT_TOKEN_STR) : void 0;
  }

  private setStoredToken(token: IAuthorizationToken) {
    const tokenString = JSON.stringify(token);
    localStorage.setItem('JWT_TOKEN_STR', tokenString);
  }

  parseJwt(tokenStrJwt: string): IAuthorizationToken {
    const base64Url = tokenStrJwt.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  }
}

export default new AuthStore();
